import * as $ from 'jquery';
import ReactDOM from 'react-dom';
import { ApplicationController } from '@aha-app/mvc';
import { type RECORD_CLASSES } from 'components/framework/models/mappings';
import NoteTemplate from 'components/framework/models/NoteTemplate';
import Note from 'components/framework/models/Note';
import Page from 'components/framework/models/Page';
import Whiteboard from 'components/framework/models/Whiteboard';
// @ts-ignore
import Turbolinks from 'javascripts/turbolinks';
import { type DocumentTypeEnum_value } from 'components/framework/models/generated/types';
import PdlcStepResource from 'javascripts/pdlc_step_resources';
import Research from 'javascripts/research';
import AhaAnalytics from 'javascripts/analytics';
import { setLocalStoragePreference } from 'javascripts/utils';
import { type RecordId } from 'javascripts/shared-types';
import {
  type GlobalNoteTemplate,
  type GlobalNoteTemplateResponse,
} from './types';
import { Project } from 'components/framework/models';
import { some } from 'lodash';
import * as messages from 'javascripts/messages';
import UserPreference from 'components/framework/models/UserPreference';

export const TEMPLATE_LOCAL_STORAGE_KEY = 'lastUsedTemplateId';

export enum TemplateCategories {
  All = 'All',
  Personal = 'Personal',
  Workspace = 'Workspace',
  Search = 'Search',
}

// TODO: CREATE-1427 changed the categories, we are temporarily including both to avoid display
// issues immediately after deploy when global templates still have the old categories.
const CATEGORIES_IN_ORDER = [
  'Meetings',
  'Teamwork',
  'Strategy',
  'Strategize', // To remove
  'Research',
  'Discovery',
  'Ideation',
  'Ideate', // To remove
  'Roadmaps',
  'Roadmap', // To remove
  'Plans',
  'Plan', // To remove
  'Designs',
  'Design', // To remove
  'Development',
  'Develop', // To remove
  'Launches',
  'Launch', // To remove
  'Reports',
  'Analyze', // To remove
  'Career growth',
];

// Templates eligible for Develop popover promo.
// Promo is shown once when any one of the eligible templates are used for the first time.
const DEVELOP_PROMO_TEMPLATE_SLUGS = [
  'safe-sprint-planning', // SAFe® Sprint planning
  'sprint-planning', // Sprint planning
  'sprint-planning-meeting', // Sprint planning meeting
  'sprint', // Sprint retrospective
  'sprint-retrospective-meeting', // Sprint retrospective meeting
];

/* eslint-disable no-unused-vars */
export enum TemplateTypes {
  all = 'all',
  note = 'note',
  whiteboard = 'whiteboard',
  meeting = 'meeting',
}
/* eslint-enable no-unused-vars */
export type TemplateType = keyof typeof TemplateTypes;

export type TemplateControllerState = {
  // Available templates.
  templates: Array<NoteTemplate | GlobalNoteTemplate>;
  // Loading the templates list
  loadingTemplates: boolean;
  // Whether controller is loading (for e.g., templates or placing a template)
  loading: boolean;
  // Currently selected category.
  currentCategory?: string;
  // Currently selected template for preview.
  currentTemplate?: NoteTemplate | GlobalNoteTemplate;
  // If preview page should be fullscreened
  fullScreen: boolean;
  // id of project page is in
  projectId?: RecordId;
  // project
  project?: Project;
  // parent page id
  parentId?: string;
  // Template type (all, note, whiteboard, meeting)
  templateType: TemplateType;
  // document position in page tree hiearchy
  position: number;
  // Date of document passed in, for pages created from calendar view
  documentDate?: string;
  // If page is created from caledar view, add to daily notes folder
  isDailyPage: boolean;
  // Override function to place template manually after selection
  placeTemplate?: (
    template: NoteTemplate | GlobalNoteTemplate,
    isGlobal: boolean
  ) => void | Promise<void>;
  // Override function to close template modal
  closeTemplateModal?: () => void;
  // Javascript function to call when the modal is closing
  afterView?: string;
  // Record to link the newly created template to
  parentRecord?: ParentRecord;
};

export const createEmptyState = (): TemplateControllerState => ({
  templates: [],
  loadingTemplates: true,
  loading: true,
  fullScreen: false,
  templateType: TemplateTypes.all,
  position: 0,
  isDailyPage: false,
});

export type TemplateControllerProps = {
  parentId?: RecordId;
  projectId?: RecordId;
  templateType: TemplateType;
  position?: number;
  documentDate?: string;
  isDailyPage?: boolean;
  initialTemplateId?: string;
  placeTemplate?: (
    template: NoteTemplate | GlobalNoteTemplate,
    isGlobal: boolean
  ) => void | Promise<void>;
  closeTemplateModal?: () => void;
  afterView?: string;
  parentRecord?: ParentRecord;
  includeExampleContent?: boolean;
  /** We reload the page to show the develop promo, when inserting the template from
   * the editor toolbar it prevents the content from being persisted. This option lets
   * us bypass the behavior for these cases.
   */
  skipDevelopPromo?: boolean;

  /** A list of template ids to load on initialization. Currently used to only
   * load the templates used in the best practices panel, rather than all
   * templates.
   */
  templateIds?: RecordId[];
};

type ParentRecord = {
  id: RecordId;
  name: string;
  type: string;
};

class TemplateController extends ApplicationController<
  TemplateControllerState,
  TemplateControllerProps
> {
  static initialState = createEmptyState();

  // List of available categories from Aha! global templates.
  categories: string[] = [];
  // Other categories, such as "All", "Custom", etc.
  customCategories = [TemplateCategories.All];
  // Track global templates
  globalTemplates: (GlobalNoteTemplate | NoteTemplate)[] = [];

  includeExampleContent = false;

  planMap = {
    wess: window.currentAccount?.whiteboardsFlavor,
    wadv: window.currentAccount?.whiteboardsAdvanced,
    kess:
      window.currentAccount?.knowledgeFlavor ||
      window.currentAccount?.ideasFlavor,
    kadv: window.currentAccount?.knowledgeAdvanced,
    dev: window.currentAccount?.teamworkOrDevelopFlavor,
  };

  async initialize({
    parentId,
    projectId,
    position,
    documentDate,
    isDailyPage,
    templateType = TemplateTypes.all,
    initialTemplateId,
    placeTemplate,
    closeTemplateModal,
    afterView,
    parentRecord,
    includeExampleContent = false,
    templateIds,
  }: TemplateControllerProps) {
    this.includeExampleContent = includeExampleContent;
    this.state.templateType = templateType || TemplateTypes.all;
    await Promise.all([
      this.loadProject(projectId ?? null),
      this.loadTemplates(projectId, templateIds),
    ]);
    this.state.loading = false;
    this.state.projectId = projectId ?? undefined;
    this.state.parentId = parentId;
    this.state.position = position ?? 0;
    this.state.documentDate = documentDate;
    this.state.isDailyPage = isDailyPage ?? false;
    // Choose All category by default.
    this.state.currentCategory = this.customCategories[0];
    // Set hooks for standalone whiteboards to interact with template modal
    this.state.placeTemplate = placeTemplate;
    this.state.closeTemplateModal = closeTemplateModal;
    this.state.afterView = afterView;
    this.state.parentRecord = parentRecord;

    if (initialTemplateId) {
      const template = this.state.templates.find(
        template => template.id === initialTemplateId
      );

      if (template) {
        this.actionSetCurrentTemplate(template);
      }
    }
  }

  /**
   * The document types to show. The scope is for the current account or the
   * global templates, and the only difference is that we do not show global
   * meeting templates when viewing all templates
   */
  allowedDocumentTypes(scope: 'local' | 'global'): DocumentTypeEnum_value[] {
    switch (this.state.templateType) {
      case 'note':
        if (window.currentAccount?.onlyWhiteboardsFlavor) return [];
        return [Page.DOCUMENT_TYPE_NOTE];
      case 'whiteboard':
        return [Page.DOCUMENT_TYPE_WHITEBOARD];
      case 'meeting':
        return [
          Page.DOCUMENT_TYPE_MEETING_NOTE,
          Page.DOCUMENT_TYPE_MEETING_WHITEBOARD,
        ];
      case 'all':
      default:
        if (scope === 'global') {
          return [Page.DOCUMENT_TYPE_NOTE, Page.DOCUMENT_TYPE_WHITEBOARD];
        } else {
          return [
            Page.DOCUMENT_TYPE_NOTE,
            Page.DOCUMENT_TYPE_WHITEBOARD,
            Page.DOCUMENT_TYPE_MEETING_NOTE,
            Page.DOCUMENT_TYPE_MEETING_WHITEBOARD,
          ];
        }
    }
  }

  async loadTemplates(
    projectId: RecordId | null = null,
    templateIds: RecordId[] | null = null
  ) {
    this.state.loadingTemplates = true;
    let projectTemplates: NoteTemplate[] = [];

    /**
     * Load project templates if we're on a project (projectId != null), or
     * personal templates (projectId == null) if we're either:
     * - In a hybrid account
     * - In a knowledge or whiteboards advanced account
     */
    if (
      projectId ||
      !window.currentAccount?.onlyNotebooksFlavor ||
      window.currentAccount?.notebooksAdvanced
    ) {
      projectTemplates = (
        await NoteTemplate.select(
          'id',
          'name',
          'categories',
          'documentType',
          'emojiValue',
          'hasLongDescription',
          'isGlobal',
          'createdAt',
          'thumbnailUrl',
          'isPersonal'
        )
          .merge({
            shortDescription: ['htmlBody'],
          })
          .where({
            projectId,
            documentType: this.allowedDocumentTypes('local'),
            isPersonal: projectId ? undefined : true,
            id: templateIds,
          })
          .findInBatches()
      ).sort((a, b) => a.name!.localeCompare(b.name!));

      (projectTemplates ?? []).forEach(template => {
        if (template.isPersonal) {
          if (!this.customCategories.includes(TemplateCategories.Personal)) {
            this.customCategories.push(TemplateCategories.Personal);
          }
          (template.categories as string[]) = [TemplateCategories.Personal];
        } else {
          if (
            projectId &&
            !this.customCategories.includes(TemplateCategories.Workspace)
          ) {
            this.customCategories.push(TemplateCategories.Workspace);
          }
          (template.categories as string[]) = [TemplateCategories.Workspace];
        }
      });
    }

    // If template type is not "all", filter on the appropriate document type
    let templateFilter = `documentType: ${JSON.stringify(
      this.allowedDocumentTypes('global')
    )}`;

    if (templateIds) {
      templateFilter += `, id: ${JSON.stringify(templateIds)}`;
    }

    const result = (await $.post(this.globalTemplatesUrl, {
      query: `{
        noteTemplates(${templateFilter}) {
          id
          name
          slug
          documentType
          categories
          plans
          hasLongDescription
          isGlobal
          shortDescription
          thumbnail
          screenshot
          createdAt
          ${
            this.includeExampleContent
              ? 'guideDescription exampleDescription exampleWhiteboard'
              : ''
          }
        }
      }`,
    }).catch(error => {
      console.error('Error fetching global templates');
      console.error(JSON.stringify(error));
      throw new Error('Failed to load templates');
    })) as { data: { noteTemplates: { nodes: GlobalNoteTemplateResponse[] } } };

    // Normalize global templates to match graphql api
    // @ts-ignore
    this.globalTemplates = result.data.noteTemplates.nodes
      .filter(template => {
        return (
          // Fail open if no plans are set/we get null/undefined
          !template.plans ||
          // Roadmaps can always see all templates
          window?.currentAccount?.roadmapFlavor ||
          some(template.plans.map(plan => this.planMap[plan]))
        );
      })
      .map(template => {
        return {
          ...template,
          longDescription: template.longDescription
            ? { htmlBody: template.longDescription }
            : undefined,
          shortDescription: { htmlBody: template.shortDescription },
        } as GlobalNoteTemplate;
      });

    const includedCategories = new Set(
      this.globalTemplates.map(template => template.categories).flat()
    );

    // Filter all categories not included in global templates
    this.categories = CATEGORIES_IN_ORDER.filter(category =>
      includedCategories.has(category)
    );

    this.state.templates = this.globalTemplates.concat(projectTemplates);
    this.state.loadingTemplates = false;
  }

  async loadProject(projectId: RecordId | null) {
    if (projectId) {
      this.state.project = await Project.select('id', 'referencePrefix')
        .merge({
          projectPermissions: ['createNoteTemplates'],
        })
        .find(projectId);
    }
  }

  linkForTemplate(template: NoteTemplate | GlobalNoteTemplate): string | null {
    const { currentProject } = window;

    // For now we only generate the URL when on a project
    if (!this.state.projectId || !currentProject) {
      return null;
    }

    return `/pages/new?template_id=${template.id}`;
  }

  actionSetCategory(category) {
    this.state.currentCategory = category;
  }

  async actionSetCurrentTemplate(
    template: NoteTemplate | GlobalNoteTemplate | undefined
  ) {
    this.state.currentTemplate = template;
    // Load template content for the detail view
    if (template) {
      this.state.loading = true;
      await this.actionLoadTemplateLongDescription();
      this.state.loading = false;
    } else if (this.state.templates.length === 1) {
      // If we're clearing the template and we currently only have one loaded,
      // we're coming from a direct link to a template. Load the full template
      // list now.
      this.state.loading = true;
      await this.loadTemplates(this.state.projectId);
      this.state.loading = false;
    }
  }

  async actionLoadTemplateLongDescription() {
    if (
      this.state.currentTemplate &&
      this.isGlobalTemplate(this.state.currentTemplate)
    ) {
      const result = await $.post(this.globalTemplatesUrl, {
        query: `{
        noteTemplate(id: "${this.state.currentTemplate.id}") {
          longDescription
        }
      }`,
      }).catch(error => {
        console.error('Error fetching global template', error);
        throw new Error('Failed to load template');
      });

      this.state.currentTemplate.longDescription = {
        // @ts-ignore
        htmlBody: result.data.noteTemplate.longDescription,
      };
    }
  }

  async loadGlobalTemplateContent(template: GlobalNoteTemplate) {
    const result = (await $.post(this.globalTemplatesUrl, {
      query: `{
      noteTemplate(id: "${template.id}") {
        content
      }
    }`,
    }).catch(error => {
      console.error('Error fetching global template', error);
      throw new Error('Failed to load template');
    })) as { data: { noteTemplate: { content: Record<string, any> } } };

    // @ts-ignore
    const content = result.data.noteTemplate.content;

    if (template.documentType === Page.DOCUMENT_TYPE_WHITEBOARD) {
      template.whiteboard = {
        editorContent: content,
      };
    } else {
      template.description = {
        editorContent: content,
      };
    }
  }

  async loadNonGlobalTemplateContent(template: NoteTemplate) {
    const query = template.query.merge(
      template.documentType === Page.DOCUMENT_TYPE_WHITEBOARD
        ? {
            whiteboard: Whiteboard.select('editorContent'),
          }
        : {
            description: Note.select('editorProperties'),
          }
    );

    await template.reload({ query });
  }

  async actionLoadTemplateContent(template: NoteTemplate | GlobalNoteTemplate) {
    if (this.isGlobalTemplate(template)) {
      await this.loadGlobalTemplateContent(template);
    } else {
      await this.loadNonGlobalTemplateContent(template);
    }

    // If a template is currently selected, update the current template
    if (this.state.currentTemplate) {
      this.state.currentTemplate = template;
    }
  }

  async actionPlaceTemplate(template: NoteTemplate | GlobalNoteTemplate) {
    const isGlobalTemplate = this.isGlobalTemplate(template);

    if (
      isGlobalTemplate &&
      !Page.DOCUMENT_TYPE_MEETING.includes(template.documentType)
    ) {
      // Save the page id to local storage, so we know to open the best practices
      // panel when the new page is loaded.
      setLocalStoragePreference(TEMPLATE_LOCAL_STORAGE_KEY, template.id);
    }

    const canTrialDevelop =
      window.currentAccount?.roadmapFlavor &&
      !window.currentAccount?.developFlavor;

    const eligibleForPromo =
      !this.props.skipDevelopPromo &&
      canTrialDevelop &&
      DEVELOP_PROMO_TEMPLATE_SLUGS.includes(template.slug) &&
      isGlobalTemplate;

    if (eligibleForPromo) {
      const promoPreference = await UserPreference.getUserPreferenceValue(
        'cross_promote.develop_template'
      );

      if (!promoPreference) {
        UserPreference.setUserPreference(
          'cross_promote.develop_template',
          'waiting'
        );
      }
    }

    // Handle placing a template inside a whiteboard
    if (this.state.placeTemplate) {
      if (!template.whiteboard?.editorContent) {
        await this.actionLoadTemplateContent(template);
      }
      await this.state.placeTemplate(template, isGlobalTemplate);
      this.actionAbortChooseTemplate();

      if (!isGlobalTemplate) {
        AhaAnalytics.track(
          'Feature-7229097699419144355',
          'User adds custom template to whiteboard'
        );
      }

      // Force page reload to show one-time promo if eligible
      if (eligibleForPromo) {
        window.location.reload();

        // Set local storage key so we won't clear out templateId from local storage
        // so best practices panel will open on reload. Only once promo has been shown
        // will we clear this value (within the sprint_template_promo_popup partial).
        setLocalStoragePreference('keepPromotedTemplateId', true);
      }

      return;
    }

    this.state.loading = true;
    const { projectId, parentId, position, documentDate, isDailyPage } =
      this.state;

    try {
      const page = await NoteTemplate.createPage({
        id: template.id,
        projectId,
        parentId,
        position,
        isGlobalTemplate,
      });

      if (documentDate) {
        page.documentDate = documentDate;
        await page.save();
      }

      if (this.parent?.actionInsertTemplatePage) {
        await this.parent.actionInsertTemplatePage(page, isDailyPage);
      } else if (this.state.afterView === 'open_research_drawer') {
        const research = new Research(
          this.state.parentRecord.id,
          this.state.parentRecord.type as keyof typeof RECORD_CLASSES,
          this.state.projectId
        );

        const record = await research.getRecord();
        const childPage = await research.createFolderStructure(record);
        page.parentId = childPage.id;
        await page.save();
        await research.createLinkAndOpenDrawer(
          page.id,
          'Page',
          `/pages/${page.id}`
        );
      } else if (this.state.afterView === 'open_pdlc_resource_drawer') {
        const resource = new PdlcStepResource(
          this.state.parentRecord.id,
          this.state.projectId
        );
        await resource.createPageRecordLink(page);
        resource.openDrawer(page.path);
      } else {
        return Turbolinks.visit(page.path);
      }
    } catch (error) {
      messages.showAlertMessage(
        'We are unable to create a document from this template. Please try again. If this problem continues, please contact support@aha.io.'
      );
    }

    this.actionAbortChooseTemplate();
  }

  actionAbortChooseTemplate() {
    this.state.currentTemplate = undefined;
    this.state.loading = false;
    if (this.state.closeTemplateModal) {
      return this.state.closeTemplateModal();
    }

    if (this.parent && Reflect.has(this.parent, 'actionHideTemplateModal')) {
      this.parent.actionHideTemplateModal();
    } else {
      const modal = document.querySelector(
        '[data-react-class=PageTemplateModal]'
      );
      if (modal) {
        ReactDOM.unmountComponentAtNode(modal);
        modal.parentNode && modal.parentNode.removeChild(modal);
      }
    }
  }

  isGlobalTemplate(
    template: NoteTemplate | GlobalNoteTemplate
  ): template is GlobalNoteTemplate {
    return this.globalTemplates.some(t => t.id === template.id);
  }

  get globalTemplatesUrl() {
    return `${window.Configatron?.secureUrlBase}/global_note_templates/graphql`;
  }
}

export default TemplateController;
